// Formatting helpers for the homepage. All times are shown in UK time.

const LONDON = 'Europe/London';

/** "Sat 8 Nov, 9.25pm" */
export function formatDraw(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', {
    timeZone: LONDON,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return `${day}, ${formatTime(iso)}`;
}

/** "9.25pm" */
export function formatTime(iso) {
  const d = new Date(iso);
  const parts = d.toLocaleTimeString('en-GB', {
    timeZone: LONDON,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // "9:25 pm" -> "9.25pm"
  return parts.replace(':', '.').replace(/\s?(am|pm)/i, (m, ap) => ap.toLowerCase());
}

/** "Saturday" / "tonight" / "tomorrow", relative to now in UK time. */
export function relativeDay(iso) {
  const key = (x) => new Date(x).toLocaleDateString('en-CA', { timeZone: LONDON });
  const target = key(iso);
  const today = key(Date.now());
  const tomorrow = key(Date.now() + 86400 * 1000);
  if (target === today) return 'tonight';
  if (target === tomorrow) return 'tomorrow';
  return new Date(iso).toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'long' });
}

export function sameLondonDay(a, b) {
  const key = (x) => new Date(x).toLocaleDateString('en-CA', { timeZone: LONDON });
  return key(a) === key(b);
}

export function money(n) {
  const v = Number(n || 0);
  return `£${v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function price(n) {
  return `£${Number(n || 0).toFixed(2)}`;
}

export function percentSold(c) {
  const total = Number(c?.totalTickets || c?.maxTickets || 0);
  const sold = Number(c?.ticketsSold || 0);
  if (!total) return 0;
  return Math.min(100, Math.round((sold / total) * 100));
}

// How wide each character of the display face is (Archivo 900 capitals, as
// set by .d), in em, measured in the browser on 14 Oct 2026. Adding them up
// slightly overstates a word (letter pairs tuck together), so a size fitted
// from them never overflows. Anything not listed counts as a wide letter.
const DISPLAY_EM = {
  "A": 0.893, "B": 0.875, "C": 0.882, "D": 0.878, "E": 0.809, "F": 0.756, "G": 0.95, "H": 0.948, "I": 0.376, "J": 0.725, "K": 0.933, "L": 0.741, "M": 1.108, "N": 0.946, "O": 0.947, "P": 0.813, "Q": 0.947, "R": 0.885, "S": 0.825, "T": 0.822, "U": 0.933, "V": 0.881, "W": 1.154, "X": 0.898, "Y": 0.901, "Z": 0.822, "0": 0.759, "1": 0.701, "2": 0.757, "3": 0.76, "4": 0.764, "5": 0.76, "6": 0.762, "7": 0.709, "8": 0.771, "9": 0.762, ".": 0.356, ",": 0.356, "'": 0.299, "’": 0.299, "&": 1.004, "£": 0.75, "-": 0.381, "!": 0.363, "?": 0.694, ":": 0.348, "%": 1.108,
};

/** The width of a word in the display face, in em. */
export function displayEm(word) {
  return [...String(word || '').toUpperCase()].reduce((sum, ch) => sum + (DISPLAY_EM[ch] ?? 0.95), 0);
}

/**
 * A display font size that never lets the longest word overflow. `max` is the
 * size wanted when there's room, e.g. "var(--t-loud)". `room` is the width
 * available: the page by default, or "100cqi" for a heading inside a column
 * that is a size container (class [container-type:inline-size]).
 */
export function fitDisplaySize(text, max, room = '100vw - 2 * var(--gutter)') {
  const longest = Math.max(
    3.2,
    ...String(text || '')
      .split(/\s+/)
      .filter(Boolean)
      .map(displayEm)
  );
  return `min(${max}, calc((${room}) / ${longest.toFixed(3)}))`;
}
